import Switch from '@/components/Switch/Switch';
import { ControlRow } from '@/pages/AdvancedEditor/components/panels/common/ControlRow';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import { checkPolotnoType } from '@/pages/AdvancedEditor/utils';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';
import { useEffect, useState } from 'react';
import { BorderColorControl, getBorderColorField, initColorState } from './BorderColorControl';
import { BorderStyleControl } from './BorderStyleControl';
import { BorderWidthControl, getBorderWidthField } from './BorderWidthControl';

const BorderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const BorderControl = observer(() => {
    const { store } = useAdvancedEditorContext();
    const elements = store.selectedElements;
    const elementJSON = elements[0]?.toJSON() as ElementType & { stroke: string; color: string; strokeWidth: number; height: number };

    const elementType = checkPolotnoType(elementJSON, polotnoTypes.Line) ? 'Stroke' : 'Border';

    const [enabled, setEnabled] = useState<boolean>(
        elementJSON.custom?.borderEnabled || checkPolotnoType(elementJSON, [polotnoTypes.Line])
    );

    useEffect(() => {
        setEnabled(elementJSON.custom?.borderEnabled || checkPolotnoType(elementJSON, [polotnoTypes.Line]));
    }, [elementJSON.id]);

    const handleSwitchChange = (_: any, checked: boolean) => {
        if (checked) {
            setEnabled(true);
            elements.forEach(element => {
                const colorField = getBorderColorField(element);
                const borderWidthField = getBorderWidthField(element);

                element.set({ custom: { borderEnabled: true }, [colorField]: initColorState(element.toJSON()[colorField]) });

                if (element.toJSON()[borderWidthField] === 0) {
                    element.set({ [borderWidthField]: 4 });
                }
            });
        } else {
            setEnabled(false);
            elements.forEach(element => {
                const borderWidthField = getBorderWidthField(element);

                element.set({ custom: { borderEnabled: false }, [borderWidthField]: 0 });
            });
        }
    };

    return (
        <BorderContainer>
            <ControlRow>
                {elementType}
                {checkPolotnoType(elementJSON, polotnoTypes.Figure) && <Switch checked={enabled} onChange={handleSwitchChange} />}
            </ControlRow>

            {enabled && (
                <>
                    <BorderColorControl />

                    <ControlRow>
                        {`${elementType} width`}
                        <BorderWidthControl />
                    </ControlRow>

                    <ControlRow>
                        {`${elementType} style`}
                        <BorderStyleControl />
                    </ControlRow>
                </>
            )}
        </BorderContainer>
    );
});
